import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import {
  HighlightedTextarea,
  type HighlightedTextareaHandle,
  placeholdersIn,
  PreviewPanel,
  UnknownVariablesNote,
  VariablesPanel,
} from '../components/TemplateTools'
import { MessageTextsEditor } from '../components/MessageTextsEditor'
import { useAdminContext } from '../session'
import type { AdminEvent, NotificationTemplate, TemplatePreview } from '../types'

/** Dernier repli du serveur (`FALLBACK_LOCALE` dans `templates.py`). */
const PLATFORM_FALLBACK_LOCALE = 'de'

/**
 * Le texte d'où partir quand la langue choisie n'a aucune version.
 *
 * Même cascade que le serveur à l'envoi (édition › plateforme, langue par
 * défaut › allemand) : le texte proposé est donc exactement celui que
 * reçoivent aujourd'hui les invités de cette langue, faute de mieux.
 */
function startingPoint(
  variants: NotificationTemplate[],
  defaultLocale: string,
): NotificationTemplate | null {
  const find = (locale: string, edition: boolean) =>
    variants.find(
      (entry) => entry.locale === locale && (entry.event_id !== null) === edition,
    )
  return (
    find(defaultLocale, true) ??
    find(defaultLocale, false) ??
    find(PLATFORM_FALLBACK_LOCALE, false) ??
    variants[0] ??
    null
  )
}

/**
 * Les messages que le système envoie.
 *
 * Ils vivaient en base depuis toujours, l'API pour les réécrire existait —
 * et aucun écran ne l'appelait. Le seul moyen de changer une phrase envoyée à
 * des centaines de personnes était de modifier le code, ce qui contredit la
 * règle du projet : le texte est de la donnée, pas du programme.
 *
 * Deux portées cohabitent, et la distinction compte :
 *
 * * **plateforme** — le modèle livré, commun à toutes les éditions ;
 * * **cette édition** — votre version, qui prend le dessus sans toucher aux
 *   autres.
 *
 * Écrire ici crée toujours une version d'édition. Le modèle de la plateforme
 * n'est jamais écrasé : on peut donc revenir en arrière en supprimant sa
 * propre version, et retrouver le texte d'origine.
 */
export function AdminMessagesPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [openCode, setOpenCode] = useState<string | null>(null)
  const [locale, setLocale] = useState(i18n.language.slice(0, 2))
  // Deux vues : les modèles (le message entier) et les textes des variables
  // (les phrases que le système glisse dans `{{order_details}}` & co.).
  const [view, setView] = useState<'templates' | 'texts'>('templates')
  const [focusGroup, setFocusGroup] = useState<string | null>(null)

  function editTexts(group: string) {
    setFocusGroup(group)
    setView('texts')
  }

  useEffect(() => {
    document.title = t('admin.section.messages')
  }, [t])

  const query = useAdminQuery<NotificationTemplate[]>(
    ['admin-templates', eventId],
    `/admin/events/${eventId}/notification-templates`,
    eventId,
  )

  // La langue par défaut de l'édition décide du texte de départ quand une
  // langue manque — c'est aussi celle que le serveur enverrait à sa place.
  const eventQuery = useAdminQuery<AdminEvent>(
    ['admin-event', eventId],
    `/admin/events/${eventId}`,
    eventId,
  )
  const defaultLocale = eventQuery.data?.default_locale ?? PLATFORM_FALLBACK_LOCALE

  const rows = useMemo(() => query.data ?? [], [query.data])

  // Un code, plusieurs langues et deux portées : on range par code, puis on
  // choisit la langue. Une liste à plat de cinquante-quatre lignes ne se lit
  // pas.
  const byCode = useMemo(() => {
    const grouped = new Map<string, NotificationTemplate[]>()
    for (const row of rows) {
      grouped.set(row.code, [...(grouped.get(row.code) ?? []), row])
    }
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [rows])

  if (query.isPending) return <Skeleton className="h-64 w-full" />
  if (query.isError) {
    return <p className="text-sm text-state-danger">{query.error.message}</p>
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.messages')}
        description={t('admin.messages.intro')}
      />

      <div
        role="tablist"
        aria-label={t('admin.messageTexts.viewLabel')}
        className="mb-4 flex flex-wrap gap-2"
      >
        {(['templates', 'texts'] as const).map((value) => (
          <Button
            key={value}
            role="tab"
            aria-selected={view === value}
            size="sm"
            variant={view === value ? 'champagne' : 'outline'}
            onClick={() => {
              setView(value)
              if (value === 'templates') setFocusGroup(null)
            }}
          >
            {value === 'templates'
              ? t('admin.messageTexts.tabTemplates')
              : t('admin.messageTexts.tabTexts')}
          </Button>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.messages.locale')}
        </span>
        {['fr', 'de', 'en'].map((code) => (
          <Button
            key={code}
            size="sm"
            variant={code === locale ? 'champagne' : 'outline'}
            onClick={() => setLocale(code)}
          >
            {code.toUpperCase()}
          </Button>
        ))}
      </div>

      {view === 'texts' ? (
        <MessageTextsEditor eventId={eventId} locale={locale} focusGroup={focusGroup} />
      ) : null}

      {/* Masquée plutôt que démontée : un modèle en cours de réécriture garde
          son brouillon le temps d'aller corriger une phrase de ses variables. */}
      <div className={cn('grid grid-cols-1 gap-2', view !== 'templates' && 'hidden')}>
        {byCode.map(([code, variants]) => {
          // La version de CETTE langue : celle de l'édition d'abord, sinon le
          // modèle livré. Il n'y a pas d'autre repli ici — l'écran se rabattait
          // sur la première langue venue, affichait son texte comme s'il
          // était celui de la langue choisie, et l'enregistrait sous cette
          // langue sans rien dire. On croyait corriger l'anglais ; on créait
          // un « anglais » rédigé en allemand.
          const exact =
            variants.find((entry) => entry.locale === locale && entry.event_id !== null) ??
            variants.find((entry) => entry.locale === locale) ??
            null
          const source = exact ?? startingPoint(variants, defaultLocale)
          if (!source) return null
          const isOverride = exact !== null && exact.event_id !== null
          return (
            <Card key={code} className="p-0">
              <button
                type="button"
                onClick={() => setOpenCode(openCode === code ? null : code)}
                aria-expanded={openCode === code}
                className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="block break-all font-mono text-caption tracking-normal text-content-muted">
                    {code}
                  </span>
                  {/* Sans version dans cette langue, l'objet affiché est celui
                      d'une autre : on le grise et on le préfixe de sa langue
                      pour qu'il ne passe pas pour une traduction. */}
                  <span
                    className={cn(
                      'mt-0.5 block truncate text-sm',
                      exact ? 'text-content-primary' : 'italic text-content-muted',
                    )}
                  >
                    {exact ? null : `${source.locale.toUpperCase()} · `}
                    {source.subject ?? '—'}
                  </span>
                </span>
                {exact ? (
                  <Badge tone={isOverride ? 'champagne' : 'neutral'}>
                    {isOverride
                      ? t('admin.messages.scopeEvent')
                      : t('admin.messages.scopePlatform')}
                  </Badge>
                ) : (
                  <span className="shrink-0 rounded-full border border-state-warning/40 px-2 py-0.5 text-[10px] uppercase text-state-warning">
                    {t('admin.messagesLocale.missingBadge')}
                  </span>
                )}
                <span aria-hidden className="shrink-0 text-content-muted">
                  {openCode === code ? '−' : '+'}
                </span>
              </button>

              {openCode === code ? (
                <TemplateEditor
                  key={`${code}:${locale}`}
                  eventId={eventId}
                  code={code}
                  locale={locale}
                  template={exact}
                  source={source}
                  onDone={() => void query.refetch()}
                  onEditTexts={editTexts}
                />
              ) : null}
            </Card>
          )
        })}
      </div>
    </>
  )
}

function TemplateEditor({
  eventId,
  code,
  locale,
  template,
  source,
  onDone,
  onEditTexts,
}: {
  eventId: string | undefined
  code: string
  locale: string
  /** La version de cette langue ; `null` = pas encore traduite. */
  template: NotificationTemplate | null
  /** Le texte chargé dans l'éditeur : `template`, ou le texte de départ. */
  source: NotificationTemplate
  onDone: () => void
  /** Ouvre « Textes des variables » sur le groupe qui compose une variable. */
  onEditTexts: (group: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [subject, setSubject] = useState(source.subject ?? '')
  const [body, setBody] = useState(source.body_text ?? '')
  const missing = template === null
  // Le nom de la langue dans la langue de l'ÉCRAN (« anglais », « Englisch ») :
  // les noms natifs (« English ») détonnaient au milieu d'une phrase française.
  const languageName = (language: string) => {
    try {
      return (
        new Intl.DisplayNames([i18n.language], { type: 'language' }).of(language) ??
        language.toUpperCase()
      )
    } catch {
      return language.toUpperCase()
    }
  }
  const targetLanguage = languageName(locale)
  const from = languageName(source.locale)
  const untouched =
    missing && subject === (source.subject ?? '') && body === (source.body_text ?? '')

  const save = useAdminMutation<
    NotificationTemplate,
    { code: string; locale: string; subject: string; body_text: string }
  >('put', () => `/admin/events/${eventId}/notification-templates`, eventId, [
    ['admin-templates', eventId ?? ''],
  ])

  const remove = useAdminMutation<{ message: string }, { id: string }>(
    'delete',
    (variables) =>
      `/admin/events/${eventId}/notification-templates/${variables.id}`,
    eventId,
    [['admin-templates', eventId ?? '']],
  )

  // Les variables que l'envoi de CE message fournit. Toutes les langues d'un
  // code reçoivent les mêmes ; seuls les exemples changent de langue.
  const variables = useMemo(
    () => (template ?? source).variables ?? [],
    [template, source],
  )
  const known = useMemo(() => new Set(variables.map((item) => item.name)), [variables])
  const cited = placeholdersIn(subject, body)
  const used = new Set(cited.filter((name) => known.has(name)))
  const unknown = cited.filter((name) => !known.has(name)).sort()

  // L'insertion va dans le dernier champ touché : « Objet » ou « Message ».
  const [target, setTarget] = useState<'subject' | 'body'>('body')
  const subjectField = useRef<HTMLInputElement>(null)
  const bodyField = useRef<HighlightedTextareaHandle>(null)
  const subjectCaret = useRef<number | null>(null)

  useLayoutEffect(() => {
    const node = subjectField.current
    if (node && subjectCaret.current !== null) {
      const caret = subjectCaret.current
      subjectCaret.current = null
      node.focus()
      node.setSelectionRange(caret, caret)
    }
  }, [subject])

  function insert(token: string) {
    if (target === 'body') {
      bodyField.current?.insert(token)
      return
    }
    const node = subjectField.current
    const start = node?.selectionStart ?? subject.length
    const end = node?.selectionEnd ?? subject.length
    subjectCaret.current = start + token.length
    setSubject(subject.slice(0, start) + token + subject.slice(end))
  }

  // L'aperçu rend le BROUILLON : on voit sa version avant de l'enregistrer,
  // donc avant qu'elle ne parte chez les clients.
  const preview = useAdminMutation<
    TemplatePreview,
    { code: string; locale: string; subject: string; body_text: string }
  >('post', () => `/admin/events/${eventId}/notification-templates/preview`, eventId)
  const [previewed, setPreviewed] = useState<{ subject: string; body: string } | null>(null)
  const showPreview = () => {
    setPreviewed({ subject, body })
    preview.mutate({ code, locale, subject, body_text: body })
  }
  const stale =
    previewed !== null && (previewed.subject !== subject || previewed.body !== body)

  const field =
    'w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary'
  const targetLabel =
    target === 'subject' ? t('admin.messages.subject') : t('admin.messages.body')

  return (
    <div className="border-t border-line-subtle px-4 py-4">
      {/* Le texte d'une autre langue ne se présente jamais comme celui-ci :
          on dit d'où il vient, ce que reçoivent les invités en attendant, et
          ce que l'enregistrement va créer. */}
      {missing ? (
        <div
          role="note"
          className="mb-4 rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-3 text-sm"
        >
          <p className="font-medium text-state-warning">
            {t('admin.messagesLocale.missingTitle', { language: targetLanguage })}
          </p>
          <p className="mt-1 text-content-secondary">
            {t('admin.messagesLocale.startingFrom', { language: from })}
          </p>
          <p className="mt-1 text-content-secondary">
            {t('admin.messagesLocale.meanwhile', { language: targetLanguage, source: from })}
          </p>
        </div>
      ) : null}

      {/* Grand écran : l'éditeur à gauche, les variables à droite. Téléphone :
          les champs, puis le panneau replié, puis les actions. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem] xl:grid-rows-[auto_1fr] xl:items-start">
        <div className="min-w-0">
          <label
            htmlFor={`${code}-${locale}-subject`}
            className="block text-caption uppercase tracking-wide text-content-muted"
          >
            {t('admin.messages.subject')}
            {missing ? ` · ${t('admin.messagesLocale.draftOf', { language: from })}` : null}
          </label>
          <input
            ref={subjectField}
            id={`${code}-${locale}-subject`}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            onFocus={() => setTarget('subject')}
            className={cn(field, 'mt-1.5')}
          />

          <label
            htmlFor={`${code}-${locale}-body`}
            className="mt-4 block text-caption uppercase tracking-wide text-content-muted"
          >
            {t('admin.messages.body')}
            {missing ? ` · ${t('admin.messagesLocale.draftOf', { language: from })}` : null}
          </label>
          <HighlightedTextarea
            ref={bodyField}
            id={`${code}-${locale}-body`}
            value={body}
            rows={12}
            known={known}
            onValueChange={setBody}
            onFocus={() => setTarget('body')}
            className="mt-1.5"
          />

          {/* Une variable mal nommée part telle quelle chez le client : on le
              dit ici, avant l'enregistrement, pas dans sa boîte mail. */}
          <UnknownVariablesNote names={unknown} />
        </div>

        <div className="min-w-0 xl:sticky xl:top-4 xl:row-span-2 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
          <VariablesPanel
            variables={variables}
            used={used}
            target={targetLabel}
            onInsert={insert}
            onEditTexts={onEditTexts}
          />
        </div>

        <div className="min-w-0">
          {save.error ? (
            <p className="mt-3 text-sm text-state-danger">{save.error.message}</p>
          ) : null}
          {remove.error ? (
            <p className="mt-3 text-sm text-state-danger">{remove.error.message}</p>
          ) : null}
          {preview.error ? (
            <p className="mt-3 text-sm text-state-danger">{preview.error.message}</p>
          ) : null}

          {/* Enregistrer tel quel recréerait précisément le défaut corrigé ici :
              une version « anglaise » rédigée dans une autre langue. Tant que rien
              n'a été retouché, le bouton attend. */}
          {untouched ? (
            <p className="mt-3 text-xs text-content-muted">
              {t('admin.messagesLocale.translateFirst', { language: targetLanguage })}
            </p>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={save.isPending || untouched}
              onClick={() =>
                save.mutate(
                  { code, locale, subject, body_text: body },
                  { onSuccess: onDone },
                )
              }
            >
              {missing
                ? t('admin.messagesLocale.create', { language: targetLanguage })
                : t('admin.messages.save')}
            </Button>

            <Button size="sm" variant="outline" disabled={preview.isPending} onClick={showPreview}>
              {t('admin.messagesVars.preview')}
            </Button>

            {/* Supprimer sa version fait réapparaître le modèle d'origine — c'est
                le seul « annuler » qui existe, et il vaut mieux qu'un bouton de
                réinitialisation qui réécrirait le texte livré.
                Seulement la version de CETTE langue : le texte de départ venu
                d'une autre langue n'est pas à supprimer d'ici. */}
            {template !== null && template.event_id !== null ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={remove.isPending}
                onClick={() => remove.mutate({ id: template.id }, { onSuccess: onDone })}
              >
                {t('admin.messages.reset')}
              </Button>
            ) : null}
          </div>

          {preview.data && previewed ? (
            <PreviewPanel
              preview={preview.data}
              stale={stale}
              refreshing={preview.isPending}
              onRefresh={showPreview}
              onClose={() => {
                setPreviewed(null)
                preview.reset()
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
